
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Edit } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import AuthorBasicInfo from './AddAuthorForm/AuthorBasicInfo';
import PaymentMethodSection from './AddAuthorForm/PaymentMethodSection';
import PasswordGenerator from './PasswordGenerator';
import { addAuthorSchema, FormData } from './AddAuthorForm/formSchema';
import { PAYMENT_METHODS } from '@/utils/constants';
import { Separator } from '@/components/ui/separator';

interface EditAuthorDialogProps {
  author: any;
}

const EditAuthorDialog: React.FC<EditAuthorDialogProps> = ({ author }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetMethod, setResetMethod] = useState<'email' | 'manual'>('email');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get current payment method and details
  const currentPaymentMethod = author.paymentMethod || {};
  const currentPaymentDetails = author.paymentDetails || {};

  const form = useForm<FormData>({
    resolver: zodResolver(addAuthorSchema),
    defaultValues: {
      name: author.name || '',
      email: author.email || '',
      phone: author.phone || '',
      address: author.address || '',
      taxPercentage: author.tax_percentage || 10,
      paymentMethod: {
        type: currentPaymentMethod.type || 'paypal',
        threshold: currentPaymentMethod.threshold || 0,
      },
      paymentDetails: {
        paypalEmail: currentPaymentDetails.paypal_email || '',
        bankName: currentPaymentDetails.bank_name || '',
        accountNumber: currentPaymentDetails.account_number || '',
        routingNumber: currentPaymentDetails.routing_number || '',
        swift: currentPaymentDetails.swift || '',
        address: currentPaymentDetails.address || '',
      },
    },
  });

  const watchedPaymentMethod = form.watch('paymentMethod.type');

  // Get threshold based on payment method
  const getThresholdForPaymentMethod = (paymentMethod: string) => {
    const method = Object.values(PAYMENT_METHODS).find(m => m.type === paymentMethod);
    return method ? method.threshold : 0;
  };

  const resetPasswordViaEmail = async () => {
    try {
      setIsResettingPassword(true);
      
      // Send password reset email to the author
      const { error } = await supabase.auth.resetPasswordForEmail(author.email, {
        redirectTo: `${window.location.origin}/auth?type=recovery`,
      });

      if (error) {
        throw error;
      }

      toast({
        title: 'Password Reset Email Sent',
        description: `A password reset email has been sent to ${author.email}. The author can use this link to set their new password.`,
      });
      
      setShowPasswordReset(false);
      setNewPassword('');
    } catch (error) {
      console.error('Error sending password reset email:', error);
      toast({
        title: 'Error',
        description: 'Failed to send password reset email. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const resetPasswordManually = async () => {
    if (!newPassword) {
      toast({
        title: 'Error',
        description: 'Please enter a password.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsResettingPassword(true);
      
      // Get the current session to include the auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session');
      }

      // Call the Edge Function to update the password
      const { data, error } = await supabase.functions.invoke('update-user-password', {
        body: {
          userId: author.id,
          newPassword: newPassword
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw error;
      }

      if (data.error) {
        throw new Error(data.error);
      }

      toast({
        title: 'Password Updated',
        description: 'The author\'s password has been updated successfully.',
      });
      
      setShowPasswordReset(false);
      setNewPassword('');
    } catch (error) {
      console.error('Error updating password:', error);
      toast({
        title: 'Error',
        description: 'Failed to update password. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handlePasswordReset = () => {
    if (resetMethod === 'email') {
      resetPasswordViaEmail();
    } else {
      resetPasswordManually();
    }
  };

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          name: data.name,
          email: data.email,
          phone: data.phone || null,
          address: data.address || null,
          tax_percentage: data.taxPercentage,
        })
        .eq('id', author.id);

      if (profileError) {
        throw profileError;
      }

      // Update or create payment method
      const threshold = getThresholdForPaymentMethod(data.paymentMethod.type);
      
      if (currentPaymentMethod.id) {
        // Update existing payment method
        const { error: paymentMethodError } = await supabase
          .from('payment_methods')
          .update({
            type: data.paymentMethod.type,
            threshold,
          })
          .eq('id', currentPaymentMethod.id);

        if (paymentMethodError) {
          throw paymentMethodError;
        }

        // Update payment details
        if (currentPaymentDetails.id) {
          const { error: detailsError } = await supabase
            .from('payment_details')
            .update({
              paypal_email: data.paymentDetails?.paypalEmail || null,
              bank_name: data.paymentDetails?.bankName || null,
              account_number: data.paymentDetails?.accountNumber || null,
              routing_number: data.paymentDetails?.routingNumber || null,
              swift: data.paymentDetails?.swift || null,
              address: data.paymentDetails?.address || null,
            })
            .eq('id', currentPaymentDetails.id);

          if (detailsError) {
            throw detailsError;
          }
        } else if (data.paymentDetails) {
          // Create new payment details
          const { error: createDetailsError } = await supabase
            .from('payment_details')
            .insert({
              author_id: author.id,
              payment_method_id: currentPaymentMethod.id,
              paypal_email: data.paymentDetails.paypalEmail || null,
              bank_name: data.paymentDetails.bankName || null,
              account_number: data.paymentDetails.accountNumber || null,
              routing_number: data.paymentDetails.routingNumber || null,
              swift: data.paymentDetails.swift || null,
              address: data.paymentDetails.address || null,
            });

          if (createDetailsError) {
            throw createDetailsError;
          }
        }
      } else {
        // Create new payment method and details
        const { data: paymentMethodData, error: paymentMethodError } = await supabase
          .from('payment_methods')
          .insert({
            author_id: author.id,
            type: data.paymentMethod.type,
            threshold,
            is_default: true,
          })
          .select()
          .single();

        if (paymentMethodError) {
          throw paymentMethodError;
        }

        if (data.paymentDetails && paymentMethodData) {
          const { error: detailsError } = await supabase
            .from('payment_details')
            .insert({
              author_id: author.id,
              payment_method_id: paymentMethodData.id,
              paypal_email: data.paymentDetails.paypalEmail || null,
              bank_name: data.paymentDetails.bankName || null,
              account_number: data.paymentDetails.accountNumber || null,
              routing_number: data.paymentDetails.routingNumber || null,
              swift: data.paymentDetails.swift || null,
              address: data.paymentDetails.address || null,
            });

          if (detailsError) {
            throw detailsError;
          }
        }
      }

      queryClient.invalidateQueries({ queryKey: ['authors'] });
      queryClient.invalidateQueries({ queryKey: ['author', author.id] });
      
      toast({
        title: 'Success',
        description: 'Author information has been updated successfully.',
      });
      
      setOpen(false);
    } catch (error) {
      console.error('Error updating author:', error);
      toast({
        title: 'Error',
        description: 'Failed to update author information. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <Edit className="h-4 w-4 mr-1" />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Author</DialogTitle>
          <DialogDescription>
            Update the author's information, tax settings, and payment preferences.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <AuthorBasicInfo form={form} />
            
            <Separator />
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">Password Management</h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPasswordReset(!showPasswordReset)}
                >
                  {showPasswordReset ? 'Cancel' : 'Reset Password'}
                </Button>
              </div>
              
              {showPasswordReset && (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                  <div className="space-y-3">
                    <div className="flex gap-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="resetMethod"
                          value="email"
                          checked={resetMethod === 'email'}
                          onChange={() => setResetMethod('email')}
                          className="text-primary"
                        />
                        <span className="text-sm">Send reset email</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="resetMethod"
                          value="manual"
                          checked={resetMethod === 'manual'}
                          onChange={() => setResetMethod('manual')}
                          className="text-primary"
                        />
                        <span className="text-sm">Set password manually</span>
                      </label>
                    </div>
                    
                    {resetMethod === 'email' ? (
                      <p className="text-sm text-muted-foreground">
                        This will send a password reset email to <strong>{author.email}</strong>.
                        The author can use the link in the email to set a new password.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          Set a new password for <strong>{author.name}</strong> directly.
                        </p>
                        <PasswordGenerator onPasswordChange={setNewPassword} />
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    onClick={handlePasswordReset}
                    disabled={isResettingPassword || (resetMethod === 'manual' && !newPassword)}
                    className="w-full"
                  >
                    {isResettingPassword 
                      ? (resetMethod === 'email' ? 'Sending Email...' : 'Updating Password...') 
                      : (resetMethod === 'email' ? 'Send Password Reset Email' : 'Update Password')
                    }
                  </Button>
                </div>
              )}
            </div>
            
            <Separator />
            
            <PaymentMethodSection form={form} />
            
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Update Author'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditAuthorDialog;
